/**
 * Feature E05-F02 — "Admin: промты и шаблоны" request/response contracts.
 *
 * 1:1 port of the legacy n8n `Prompts` group (docs/source-n8n/reference/Prompts.md
 * + sql-queries.md "Prompts: Query All" / "Prompts: Execute Update"). This is the
 * single place in the system where `ai_prompts` is WRITTEN; every other group only
 * READS prompts by `prompt_key` (the "prompts only from DB" pattern).
 *
 * Two operations only — there is NO create/delete: prompts are seeded by the
 * migrations (legacy migration 009), the admin SPA can only LIST and partially
 * UPDATE one prompt by its `prompt_key`:
 *
 *   get_prompts    → GET   /admin/prompts          (full list, no filter, ordered)
 *   update_prompt  → PATCH /admin/prompts/:promptKey (PATCH — only provided fields)
 *
 * Invariants carried over unchanged:
 *   - get_prompts returns ALL rows (incl. is_active=false), ORDER BY stage ASC,
 *     prompt_key ASC (the group order in the editor UI);
 *   - update is keyed by `prompt_key` (UNIQUE), NOT by id;
 *   - PATCH semantics: a column is updated ONLY if the field is present in the
 *     body (`undefined` is skipped); `max_tokens` null/0 → set NULL;
 *   - `updated_at = NOW()` and `updated_by = <session user>` are ALWAYS written;
 *   - stage / action / input_variables / output_format are NOT updatable here;
 *   - `updated_by` comes from the SESSION, never the body (IDOR rule).
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// T01 — get_prompts list DTO (Prompts: Query All shape)
// ---------------------------------------------------------------------------
/**
 * One prompt row, mirroring the legacy `Prompts: Query All` SELECT:
 *
 *   SELECT id, prompt_key, title, description, stage, action, prompt_template,
 *          input_variables, output_format, max_tokens, primary_model, fallback_model,
 *          is_active, updated_at
 *   FROM ai_prompts ORDER BY stage ASC, prompt_key ASC
 *
 * `input_variables` is jsonb in the DB and is returned as-is — an array of the
 * variable names the template interpolates (`{{clientName}}` etc.). It is coerced
 * to a string[] for the contract; any non-array jsonb collapses to `[]` so the UI
 * chip-list never crashes.
 */
export const adminPromptSchema = z.object({
    id: z.number().int(),
    prompt_key: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    stage: z.number().int().nullable(),
    action: z.string().nullable(),
    prompt_template: z.string(),
    /** jsonb array of variable names interpolated by the template. */
    input_variables: z.array(z.string()),
    output_format: z.string().nullable(),
    max_tokens: z.number().int().nullable(),
    primary_model: z.string(),
    fallback_model: z.string(),
    /** `is_active` is nullable in the schema; the UI treats null as active. */
    is_active: z.boolean().nullable(),
    /** ISO 8601 timestamp. */
    updated_at: z.string(),
});
/** GET /admin/prompts response — ALL prompts, ordered stage ASC, prompt_key ASC. */
export const adminPromptsListResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    prompts: z.array(adminPromptSchema),
});
// ---------------------------------------------------------------------------
// T01 — update_prompt PATCH body (Prompts: Build Update SQL inputs)
// ---------------------------------------------------------------------------
/**
 * PATCH body for update_prompt. Every field is OPTIONAL (PATCH semantics —
 * only present fields are written). `promptKey` itself is NOT in the body: it is
 * the path param `:promptKey` (the legacy `body.promptKey`, mandatory).
 *
 * Updatable columns (legacy `Prompts: Build Update SQL`):
 *   title, description, prompt_template, primary_model, fallback_model — strings;
 *   max_tokens — int | null (legacy: parseInt, falsy → NULL);
 *   is_active — boolean.
 *
 * NOT updatable here (matching the legacy node): stage, action,
 * input_variables, output_format. `updated_by` is never accepted from the body
 * — it is taken from the session (IDOR rule), so it is intentionally absent.
 *
 * `.strict()` rejects unknown keys so a forged `updated_by` / `stage` / `id`
 * cannot sneak through; `max_tokens` accepts `null` to mean "set NULL".
 */
export const adminPromptUpdateBodySchema = z
    .object({
    title: z.string().optional(),
    description: z.string().optional(),
    prompt_template: z.string().optional(),
    primary_model: z.string().min(1).optional(),
    fallback_model: z.string().min(1).optional(),
    max_tokens: z.number().int().nullable().optional(),
    is_active: z.boolean().optional(),
})
    .strict();
/** `:promptKey` path param — non-empty, ≤100 chars (varchar(100) UNIQUE). */
export const adminPromptKeyParamSchema = z.object({
    promptKey: z.string().min(1).max(100),
});
/**
 * Descriptor of the pre-overwrite snapshot a save produced (E05-F08-T06).
 *
 * `reason` is `tuning:<user>:<YYYY-MM-DD>` and `backed_up_at` the exact moment —
 * together they are the coordinates by which the archived text is found in
 * `ai_prompts_backup`. Reported so the editor can say "previous version saved"
 * without a second round-trip and without direct DB access.
 */
export const adminPromptSnapshotSchema = z.object({
    reason: z.string(),
    backed_up_at: z.string(),
});
/**
 * PATCH /admin/prompts/:promptKey response. Echoes the updated row's
 * `prompt_key` (legacy `RETURNING prompt_key`). A `prompt_key` that matched 0
 * rows is a 404 (NOT_FOUND) — the legacy `success` ambiguity (gotcha in
 * Prompts.md) is resolved explicitly in v2.
 *
 * `snapshot` is `null` when the save archived nothing: the text did not change,
 * the patch never carried `prompt_template`, or the previous wording was already
 * archived (see `services/adminPrompts.ts` → `decidePromptSnapshot`). Optional in
 * the schema so a client parsing an older payload keeps working.
 */
export const adminPromptUpdateResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    prompt_key: z.string(),
    snapshot: adminPromptSnapshotSchema.nullable().optional(),
});
export function parseInputVariables(raw) {
    const trimmed = raw.trim();
    // Empty field → treated as an empty list (legacy: falsy skips JSON.parse).
    if (trimmed.length === 0)
        return { ok: true, value: [] };
    try {
        const parsed = JSON.parse(trimmed);
        if (!Array.isArray(parsed))
            return { ok: false, error: 'invalid-json' };
        // Coerce every entry to string (the chips are variable-name strings).
        return { ok: true, value: parsed.map((v) => String(v)) };
    }
    catch {
        return { ok: false, error: 'invalid-json' };
    }
}
/** Toast message shown when `input_variables` is not valid JSON (legacy parity). */
export const INVALID_VARIABLES_JSON_MESSAGE = 'Invalid JSON in variables';
//# sourceMappingURL=admin-prompts.js.map